import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Clock, Package, CheckCircle2, Lightbulb, Download, Video, ArrowRight, Heart, CheckCheck, FileArchive, Lock, Crown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useI18n } from '@/i18n';
import { getCurrentTier, canAccess, TIERS } from '@/tiers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ExerciseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useI18n();
  const [exercise, setExercise] = useState(null);
  const [relatedExercises, setRelatedExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [translationFallback, setTranslationFallback] = useState(false);

  useEffect(() => {
    const fetchExercise = async () => {
      setLoading(true);
      setTranslationFallback(false);
      try {
        // Fetch translated exercise based on current language
        const url = lang === 'en'
          ? `${API}/exercises/${id}`
          : `${API}/exercises/${id}/translate?lang=${lang}`;
        const response = await axios.get(url);
        setExercise(response.data);
        if (response.data._translation_status === 'fallback_english') {
          setTranslationFallback(true);
        }

        // Fetch related exercises (same skill) - keep English for the cards (title only)
        const relatedResponse = await axios.get(`${API}/exercises?skill=${encodeURIComponent(response.data.skill)}`);
        const related = relatedResponse.data.filter(ex => ex.id !== id).slice(0, 3);
        setRelatedExercises(related);

        // Load favorite status from localStorage
        const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
        setIsFavorite(favorites.includes(id));

        // Load completed steps from localStorage
        const allCompleted = JSON.parse(localStorage.getItem('completedSteps') || '{}');
        setCompletedSteps(allCompleted[id] || []);
      } catch (error) {
        console.error('Error fetching exercise:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchExercise();
  }, [id, lang]);

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    if (isFavorite) {
      const updated = favorites.filter(fav => fav !== id);
      localStorage.setItem('favorites', JSON.stringify(updated));
      setIsFavorite(false);
      toast.success('Removed from favorites');
    } else {
      favorites.push(id);
      localStorage.setItem('favorites', JSON.stringify(favorites));
      setIsFavorite(true);
      toast.success('Added to favorites! ⭐');
    }
  };

  const toggleStepComplete = (stepIndex) => {
    let newCompleted;
    if (completedSteps.includes(stepIndex)) {
      newCompleted = completedSteps.filter(s => s !== stepIndex);
    } else {
      newCompleted = [...completedSteps, stepIndex];
      // Celebration when all steps complete
      if (newCompleted.length === exercise.steps.length) {
        toast.success('🎉 Exercise Complete! Great job!', {
          duration: 4000,
        });
      }
    }
    setCompletedSteps(newCompleted);

    const allCompleted = JSON.parse(localStorage.getItem('completedSteps') || '{}');
    allCompleted[id] = newCompleted;
    localStorage.setItem('completedSteps', JSON.stringify(allCompleted));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xl text-gray-600">Loading exercise...</p>
        </div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-black text-gray-900 mb-4">Exercise Not Found</h1>
          <p className="text-xl text-gray-600 mb-8">The exercise you're looking for doesn't exist.</p>
          <Link to="/exercises">
            <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Exercise Library
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Tier gate: block access if user's tier is below the exercise tier
  const userTier = getCurrentTier();
  if (!canAccess(userTier, exercise.tier)) {
    const isPro = exercise.tier === 'pro';
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-blue-50 py-16 px-4 flex items-center justify-center">
        <Card className="max-w-md w-full border-4 border-orange-400 shadow-2xl bg-gradient-to-br from-orange-50 to-yellow-50">
          <CardContent className="p-10 text-center space-y-6">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center shadow-xl">
              <Lock className="h-10 w-10 text-white" />
            </div>
            <div className="space-y-2">
              <Badge className={`${isPro ? 'bg-orange-500' : 'bg-purple-600'} text-white border-0 font-bold`}>
                {isPro ? (
                  <><Crown className="h-3 w-3 mr-1" />{t('proPlan')}</>
                ) : (
                  <><Package className="h-3 w-3 mr-1" />{t('ultimatePlan')}</>
                )}
              </Badge>
              <h2 className="text-3xl font-black text-gray-900">{t('exerciseLockedTitle')}</h2>
              <p className="text-gray-700">{t('exerciseLockedMsg')}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border-2 border-orange-200">
              <p className="text-sm text-gray-500 mb-1">{exercise.skill} • {exercise.difficulty}</p>
              <p className="font-bold text-lg text-gray-900">{exercise.title}</p>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/pricing')}
                className={`${isPro ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'} text-white font-bold py-6 text-lg shadow-lg`}
                data-testid="upgrade-from-locked"
              >
                <Crown className="mr-2 h-5 w-5" />
                {isPro ? t('upgradeToPro') : t('upgradeToUltimate')}
              </Button>
              <Link to="/exercises">
                <Button variant="outline" className="w-full border-2 border-orange-300 font-semibold">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('browseExercises')}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDownloadPDF = async () => {
    const sessionId = localStorage.getItem('pro_session_id');
    const unlocked = localStorage.getItem('pro_unlocked') === 'true';
    if (!sessionId || !unlocked) {
      toast.info('Unlock the Pro Pack to download drill cards');
      navigate('/pricing');
      return;
    }
    try {
      const response = await axios.get(`${API}/exercises/${id}/pdf?session_id=${sessionId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${id}_drill_card.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Drill card downloaded! 📄');
    } catch (e) {
      console.error(e);
      if (e.response?.status === 402) {
        toast.error('Payment required. Please purchase the Pro Pack.');
        navigate('/pricing');
      } else {
        toast.error('Download failed. Please try again.');
      }
    }
  };

  // Build Amazon affiliate search link for equipment
  const affiliateLink = (item) => {
    const tag = 'batbetter-20';
    const query = encodeURIComponent(`baseball ${item}`);
    return `https://www.amazon.com/s?k=${query}&tag=${tag}`;
  };

  const handleDownloadBundleZip = async () => {
    const bundleSession = localStorage.getItem('bundle_session_id');
    if (!bundleSession) {
      toast.info('Upgrade to Bundle Pack to download all PDFs as ZIP');
      navigate('/pricing');
      return;
    }
    try {
      const response = await axios.get(`${API}/exercises/bundle/zip?session_id=${bundleSession}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `youth_baseball_drill_cards_bundle.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bundle ZIP downloaded! 📦');
    } catch (e) {
      console.error(e);
      toast.error('Bundle download failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-blue-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6 text-orange-600 hover:text-orange-700 hover:bg-orange-50 font-semibold"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back')}
        </Button>

        {translationFallback && (
          <div className="mb-4 p-3 rounded-lg bg-yellow-50 border-2 border-yellow-200 text-sm text-yellow-800 text-center">
            ⚠️ {t('translationFallback')}
          </div>
        )}

        {/* Exercise Header */}
        <Card className="border-2 border-orange-400 shadow-xl bg-white mb-8">
          <CardHeader className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 font-semibold border border-orange-300 text-sm px-3 py-1">
                  {exercise.skill}
                </Badge>
                <Badge 
                  variant="outline" 
                  className={`font-semibold text-sm px-3 py-1 ${
                    exercise.difficulty === 'Beginner' ? 'border-green-500 text-green-700 bg-green-50' :
                    exercise.difficulty === 'Intermediate' ? 'border-blue-500 text-blue-700 bg-blue-50' :
                    'border-red-500 text-red-700 bg-red-50'
                  }`}
                >
                  {exercise.difficulty}
                </Badge>
              </div>
              <Button 
                onClick={handleDownloadPDF}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold shadow-md hover:shadow-lg transition-all duration-200"
                data-testid="download-drill-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                {t('downloadDrillCard')}
              </Button>
              {localStorage.getItem('bundle_unlocked') === 'true' && (
                <Button
                  onClick={handleDownloadBundleZip}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold shadow-md hover:shadow-lg transition-all duration-200"
                  data-testid="download-zip-btn"
                >
                  <FileArchive className="mr-2 h-4 w-4" />
                  {t('downloadAllZip')}
                </Button>
              )}
              <Button 
                onClick={toggleFavorite}
                variant="outline"
                className={`font-bold border-2 transition-all duration-200 ${
                  isFavorite 
                    ? 'border-red-500 bg-red-50 text-red-600 hover:bg-red-100' 
                    : 'border-gray-300 hover:border-red-400 hover:bg-red-50'
                }`}
                data-testid="favorite-btn"
              >
                <Heart className={`mr-2 h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
                {isFavorite ? t('favorited') : t('favorite')}
              </Button>
            </div>
            
            <CardTitle className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">
              {exercise.title}
            </CardTitle>
            
            <CardDescription className="text-lg text-gray-700 leading-relaxed">
              {exercise.description}
            </CardDescription>

            <div className="flex flex-wrap gap-6 pt-2">
              <div className="flex items-center gap-2 text-gray-700">
                <Clock className="h-5 w-5 text-orange-600" />
                <span className="font-semibold">{exercise.duration}</span>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Video Section */}
            {exercise.videoUrl && (
              <Card className="border-2 border-gray-200 shadow-lg bg-white overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-red-500 to-orange-500 text-white">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Video className="h-5 w-5" />
                    {t('videoDemo')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="aspect-video bg-gray-900">
                    <iframe
                      width="100%"
                      height="100%"
                      src={exercise.videoUrl}
                      title={exercise.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Steps */}
            <Card className="border-2 border-gray-200 shadow-lg bg-white">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                <CardTitle className="flex items-center justify-between text-xl">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    {t('stepByStep')}
                  </span>
                  <span className="text-sm bg-white/20 px-3 py-1 rounded-full">
                    {completedSteps.length}/{exercise.steps.length} {t('done')}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500 rounded-full"
                      style={{ width: `${(completedSteps.length / exercise.steps.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <ol className="space-y-3">
                  {exercise.steps.map((step, index) => {
                    const isComplete = completedSteps.includes(index);
                    return (
                      <li 
                        key={index} 
                        onClick={() => toggleStepComplete(index)}
                        data-testid={`step-${index}`}
                        className={`flex gap-4 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                          isComplete 
                            ? 'bg-green-50 border-2 border-green-200' 
                            : 'hover:bg-blue-50 border-2 border-transparent'
                        }`}
                      >
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-200 ${
                          isComplete
                            ? 'bg-green-500 text-white'
                            : 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white'
                        }`}>
                          {isComplete ? <CheckCheck className="h-5 w-5" /> : index + 1}
                        </div>
                        <p className={`leading-relaxed pt-1 text-base flex-1 ${
                          isComplete ? 'text-gray-500 line-through' : 'text-gray-800'
                        }`}>
                          {step}
                        </p>
                      </li>
                    );
                  })}
                </ol>
                {completedSteps.length === exercise.steps.length && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-lg text-center border-2 border-yellow-300">
                    <p className="text-2xl font-black text-orange-600">{t('exerciseComplete')}</p>
                    <p className="text-gray-700 mt-1">{t('keepPracticing')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="border-2 border-yellow-300 shadow-lg bg-gradient-to-br from-yellow-50 to-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl text-gray-900">
                  <Lightbulb className="h-5 w-5 text-yellow-600" />
                  {t('proTips')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {exercise.tips.map((tip, index) => (
                    <li key={index} className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-800 leading-relaxed text-base">{tip}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Equipment */}
            <Card className="border-2 border-gray-200 shadow-lg bg-white">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Package className="h-5 w-5" />
                  {t('equipmentNeeded')}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {exercise.equipment.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 group">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div className="flex-1">
                        <span className="text-gray-800 font-medium">{item}</span>
                        <a
                          href={affiliateLink(item)}
                          target="_blank"
                          rel="noopener noreferrer sponsored"
                          className="block text-xs text-orange-600 hover:text-orange-700 hover:underline mt-0.5"
                          data-testid={`affiliate-${index}`}
                        >
                          {t('shopAmazon')}
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-400 mt-4 italic">{t('affiliateDisclosure')}</p>
              </CardContent>
            </Card>

            {/* Quick Info */}
            <Card className="border-2 border-orange-300 shadow-lg bg-gradient-to-br from-orange-50 to-yellow-50">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900">{t('quickInfo')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">{t('skillFocus')}</p>
                  <p className="text-base font-bold text-orange-700">{exercise.skill}</p>
                </div>
                <Separator />
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">{t('difficulty')}</p>
                  <p className="text-base font-bold text-orange-700">{exercise.difficulty}</p>
                </div>
                <Separator />
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">{t('duration')}</p>
                  <p className="text-base font-bold text-orange-700">{exercise.duration}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Related Exercises */}
        {relatedExercises.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-black text-gray-900 mb-8">{t('moreExercises')} {exercise.skill}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedExercises.map((relatedEx) => (
                <Card key={relatedEx.id} className="border-2 hover:border-orange-400 transition-all duration-200 hover:shadow-xl transform hover:-translate-y-1 bg-white">
                  <CardHeader>
                    <Badge 
                      variant="outline" 
                      className={`w-fit font-semibold mb-2 ${
                        relatedEx.difficulty === 'Beginner' ? 'border-green-500 text-green-700 bg-green-50' :
                        relatedEx.difficulty === 'Intermediate' ? 'border-blue-500 text-blue-700 bg-blue-50' :
                        'border-red-500 text-red-700 bg-red-50'
                      }`}
                    >
                      {relatedEx.difficulty}
                    </Badge>
                    <CardTitle className="text-lg font-bold text-gray-900">{relatedEx.title}</CardTitle>
                    <CardDescription className="text-sm line-clamp-2">{relatedEx.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={`/exercise/${relatedEx.id}`}>
                      <Button className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold">
                        {t('viewExercise')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};